/**
 * Core / Plugin: Helpers
 * -----------------------------------------------------------------------------
 * Vue plugin to extend Vue instance with global helpers.
 * - Add in alphabetical order.
 *
 */
import { formatMoney } from '@shopify/theme-currency'

import classes from '~/config/classes'
import { gtmTrack } from '~/helpers/analytics'

/**
 * Expose install method.
 */
export default {
  install: (app) => {

    /**
     * Get block class (if passed as prop).
     * @param {String} blockClass - Block class to add to element.
     * @param {String} element - Element class to add to element.
     * @param {String} modifier - Modifier class to add to element.
     * @returns {String}
     */
    app.config.globalProperties.$blockClass = (blockClass, element, modifier) => {
      if (!blockClass) {
        return ''
      }

      let elementClass = blockClass

      if (element) {
        elementClass += `__${element}`
      }

      if (modifier) {
        elementClass += `--${modifier}`
      }

      return elementClass
    }

    /**
     * Finds class/state in `config/classes.js`.
     * @param {String} state - Class/state to find.
     * @returns {String}
     */
    app.config.globalProperties.$class = (state) => {
      if (!classes[state]) {
        throw new Error(`No '${state}' found in 'config/classes.js'`)
      }

      return classes[state]
    }

    /**
     * Strips paragraph tags out of rich text content and maintains line breaks.
     * @param {String} content - Rich text content to strip
     * @param {Object} options - Options to apply to the rte content
     * @param {Boolean} options.wrapLineBreaks - If content has line breaks, wrap each line with a span tag
     * @returns {string}
     */
    app.config.globalProperties.$cleanRTE = (
      content = '',
      options = {
        wrapLineBreaks: false,
        useTextLink: false,
      },
    ) => {
      let newContent = content
      let spanClose = ''
      let spanOpen = ''
      const { wrapLineBreaks, useTextLink } = options

      if (wrapLineBreaks) {
        spanClose = '</span>'
        spanOpen = '<span>'
      }

      newContent = newContent
        .replaceAll('</p><p>', `${spanClose}<br>${spanOpen}`)
        .replaceAll('<p>', spanOpen)
        .replaceAll('</p>', spanClose)

      if (useTextLink) {
        const linksRegex = new RegExp('<a (.*)>(.*)<\/a>', 'g')
        newContent = newContent.replaceAll(linksRegex, '<a class="button button--text" $1><span class="button__label">$2</span></a>')
      }

      return newContent
    }

    /**
     * Formats money into store format.
     * @param {Number} value - Value in cents.
     * @param {String} format - Money format.
     * @param {Boolean} roundDecimals - Allow or disallow {{amount_no_decimals}}
     * from rounding decimals. This will imitate Shopify's
     * money_without_trailing_zeros filter.
     * @param {Boolean} noTrailingZeroes - Remove trailing zeroes regardless
     * of format.
     * @returns {String}
     */
    app.config.globalProperties.$formatMoney = (value, format = cnvs.store.moneyFormat, roundDecimals = false, noTrailingZeroes = true) => {
      const money = formatMoney(value, format)

      if (format === '${{amount_no_decimals}}' && !roundDecimals) {
        return formatMoney(value, '${{amount}}').replace('.00', '')
      }

      if (noTrailingZeroes) {
        return money.replace('.00', '')
      }

      return money
    }

    /**
     * Retrieve icon from theme icons.
     * @param {String} path - Path to icon defined in `theme-icons.liquid`,
     * excluding cnvs.icons, can be nested, for example `directional.arrow_left`.
     */
    app.config.globalProperties.$icon = (path) => {
      let icon = cnvs.icons

      path.split('.').forEach((part) => {
        icon = icon[part]
      })

      if (!icon) {
        window.console.log(`No icon found at 'cnvs.icons.${path}'`)
        return 'No icon found'
      }

      return icon
    }

    app.config.globalProperties.$gtmTrack = gtmTrack

    /**
     * Retrieve langauge string from theme strings.
     * @param {String} path - Path to string defined in `theme-strings.liquid`,
     * excluding cnvs.strings, for example `accessibility.close`.
     * @param {Object} [data] - Data object.
     * @param {Number} [data.pluralise] - Number used to determine whether the
     * single or plural string is used in the provided path.
     * @param {Object} [data.replace] - Key: value pairs of text to replace
     * in the string, e.g. { label: 'New' } would replace `{{ label }}` in the
     * string.
     * @returns {String}
     */
    app.config.globalProperties.$string = (path, { pluralise, replace = {} } = {}) => {
      let string = cnvs.strings

      path.split('.').forEach((part) => {
        string = string[part]
      })

      if (!string) {
        window.console.log(`No string found at 'cnvs.strings.${path}'`)
        return 'No string found'
      }

      /**
       * If pluralise then update string to use single or plural items.
       * - Assumes provided path is object in format { single: '', plural: '' }
       */
      const correctStringType = (
        typeof string === 'object' ||
        string.includes('=&gt;')
      )

      if (typeof pluralise === 'number' && correctStringType) {
        if (typeof string !== 'object') {
          string = string
            .replace(/[=]&gt;/g, ':')
            .replace(/&quot;/g, '"')

          string = JSON.parse(string)
        }

        string = (pluralise === 1)
          ? string.single
          : string.plural
      }

      /**
       * Go through each each replacement and replace string with value.
       */
      if (Object.keys(replace).length) {
        Object.keys(replace).forEach((key) => {
          const regex = new RegExp(`{{ ${key} }}`, 'g')
          string = string.replace(regex, replace[key])
        })
      }

      /**
       * Replace escaped characters.
       */
      string = string
        .replace(/&#39;/g, '\'')
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/[=]&gt;/g, ':')

      return string
    }

    /**
     * Retrieve variable from theme strings.
     * @param {String} path - Path to variable stored in
     * `theme-variables.liquid`, excluding cnvs, for example `routes.cart.add`.
     * @returns {*}
     */
    app.config.globalProperties.$variable = (path) => {
      let variable = cnvs

      path.split('.').forEach((part) => {
        variable = variable[part]
      })

      return variable
    }
  },
}
